import { useState, useEffect } from 'react';
import { FormGroup, Label, Input } from 'reactstrap';
import type { RetryPolicy } from '../../api/dialogTemplates';
import { VpSelect } from '../ui/VpSelect';

export interface RetryPolicyFormProps {
  value: RetryPolicy;
  onChange: (next: RetryPolicy) => void;
  readOnly?: boolean;
}

const ON_LOW = ['retry', 'escalate', 'accept'] as const;
const ON_MAX = ['fallback', 'cancel', 'hand_off'] as const;

export function RetryPolicyForm({ value, onChange, readOnly = false }: RetryPolicyFormProps) {
  const [maxRetriesRaw, setMaxRetriesRaw] = useState(String(value.max_retries));
  const [thresholdRaw, setThresholdRaw] = useState(String(value.low_confidence_threshold));

  useEffect(() => { setMaxRetriesRaw(String(value.max_retries)); }, [value.max_retries]);
  useEffect(() => { setThresholdRaw(String(value.low_confidence_threshold)); }, [value.low_confidence_threshold]);

  const update = <K extends keyof RetryPolicy>(k: K, v: RetryPolicy[K]) => onChange({ ...value, [k]: v });

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      <FormGroup className="mb-0">
        <Label for="retry-max" className="mb-1">Max retries</Label>
        <Input
          id="retry-max"
          type="number"
          aria-label="Max retries"
          min={0} max={5}
          value={maxRetriesRaw}
          onChange={(e) => {
            if (readOnly) return;
            setMaxRetriesRaw(e.target.value);
            const n = parseInt(e.target.value, 10);
            if (!isNaN(n)) update('max_retries', Math.max(0, Math.min(5, n)));
          }}
          disabled={readOnly}
        />
      </FormGroup>
      <FormGroup className="mb-0">
        <Label for="retry-threshold" className="mb-1">Low confidence threshold</Label>
        <Input
          id="retry-threshold"
          type="number"
          aria-label="Low confidence threshold"
          step={0.05} min={0} max={1}
          value={thresholdRaw}
          onChange={(e) => {
            if (readOnly) return;
            setThresholdRaw(e.target.value);
            const n = parseFloat(e.target.value);
            if (!isNaN(n)) update('low_confidence_threshold', Math.max(0, Math.min(1, n)));
          }}
          disabled={readOnly}
        />
      </FormGroup>
      <FormGroup className="mb-0">
        <Label className="mb-1">On low confidence</Label>
        <VpSelect
          ariaLabel="On low confidence" isDisabled={readOnly}
          options={ON_LOW.map((o) => ({ value: o, label: o }))}
          value={value.on_low_confidence}
          onChange={(v) => update('on_low_confidence', v as RetryPolicy['on_low_confidence'])}
        />
      </FormGroup>
      <FormGroup className="mb-0">
        <Label className="mb-1">On max retries</Label>
        <VpSelect
          ariaLabel="On max retries" isDisabled={readOnly}
          options={ON_MAX.map((o) => ({ value: o, label: o }))}
          value={value.on_max_retries}
          onChange={(v) => update('on_max_retries', v as RetryPolicy['on_max_retries'])}
        />
      </FormGroup>
    </div>
  );
}
